// config-templates.ts — the Configuration Templates screens (owner item 65,
// PR 2): the management list (#/config-templates) and the editor
// (#/config-templates/<id>).
//
// The owner set the bar on the EDITOR: "absolutely first class in terms of
// how it's organized … better than how it's implemented in the helm". The
// Helm's Settings modal is one scrolling pile of panes behind tabs; this is
// a full PAGE built for a 156-variable surface:
//   - a sticky section nav (19 sections) with per-section counts: changed-
//     from-default and unsaved-edit badges, live while you type;
//   - search across every setting (label, key, help text, seat) that prunes
//     sections to their hits and keeps the nav honest about it;
//   - per-control types from the catalog (toggle / select / number / text /
//     json), each with its bounds and option labels — never a raw JSON
//     textarea for a scalar;
//   - "changed from default" marked per row, with the default named and a
//     one-click reset;
//   - dirty tracking with an explicit save: Review & save shows the exact
//     old -> new rows the audit trail will record (pendingDiff mirrors the
//     server's diff), so what you approve is what is written.
//
// Every judgment (filtering, diffing, pruning, action offering, value
// parsing) lives in config-templates-vm.ts — pure, bundled alone by the
// headless suite and drilled against the real served catalog. This module
// only renders those decisions and talks to the routes.
//
// The client hides what the server would refuse but never decides: a
// member gets the read-only view; every refusal is rendered VERBATIM
// (refusal names like default-cannot-be-disabled are the contract).

import {
  absoluteTime, el, jsonFetch, makeGrid, relativeTime, renderRefusal,
} from './admin-ui.js';
import type { ColDef, GridApi } from 'ag-grid-community';
import { confirmDialog, destroyDialog, noticeDialog, openDialog, showDialogMsg } from './dialog.js';
import { canManageTemplates, mountShell } from './shell.js';
import {
  auditRowVm, displayValue, overrideCount, rowActions, templateBadges, templateRowMenuSpec,
  type CatSection, type TemplateRow,
} from './config-templates-vm.js';
import { mountConfigEditor, type ConfigEditorHandle } from 'pi-stack/client/config-editor.js';
import { HC_VOCAB } from 'pi-stack/client/modal-host.js';
import { iconBtn, mkIconBtn, setBtnState } from './buttons.js';
import { mkFcText } from './form-controls.js';
import { adminGridMachinery } from './admin-grid.js';
import { gridRowsToCards, renderCardList, watchPhone } from './card-list.js';
import { ADMIN_GRID_TOOLBAR, renderGridToolbarButtons } from './grid-toolbar.js';
import { displayValueFromDefs } from './grid-machinery.js';
import { fmtWhen } from './home.js';
import { icon } from './generated/icons.js';
import { mkSearchBox } from './search.js';
import type { CtxMenuItem } from './ctxmenu.js';

interface Refusal {
  refusal?: string;
  error?: string;
}

const refusalText = (status: number, body: Refusal | null): string =>
  body?.error ?? (body?.refusal ? body.refusal : `Refused (${status})`);

// ---------------------------------------------------------------------
// bridge-ui-polish PR 3 — the list grid's machinery seats (item E) and the
// ONE change-log dialog both screens open (item C6).
// ---------------------------------------------------------------------

/** Canonical column order — what reconcileColumnState re-homes a stale
 * saved preset against (must match columns()'s colIds; the headless suite
 * pins both against each other). */
export const TEMPLATES_COLUMN_IDS = ['name', 'description', 'overrides', 'updated', 'actions'] as const;

/** No shipped pins — the users/audit R59a decision: a viewport-fitting
 * grid gains nothing from a default pin. */
export const DEFAULT_TEMPLATES_PINS: readonly string[] = [];

/** The eye's vocabulary for one cell: what the per-column filter lists,
 * Copy emits and doesFilterPass matches. Two overrides where the cell
 * paints something the raw valueGetter does not say: the overrides count's
 * sentence, and the datetime column (fmtWhen — home.ts's exact trade).
 * Exported pure for the headless suite. */
export function templatesDisplayValue(
  defs: ReadonlyArray<ColDef<TemplateRow>>,
  colId: string,
  row: TemplateRow,
  catalog: CatSection[],
): string {
  switch (colId) {
    case 'overrides': {
      const n = overrideCount(catalog, row.sections);
      return n ? `${n} setting${n === 1 ? '' : 's'}` : 'pure defaults';
    }
    case 'updated': {
      const s = fmtWhen(row.updated_at);
      return s === '—' ? '' : s;
    }
    default:
      return displayValueFromDefs(defs, colId, row);
  }
}

/** Item C6 — the change-log dialog, ONE implementation: the editor's
 * History button and the list's clickable Updated cell (and its row-menu
 * entry) all open THIS. History outlives the catalog: a change on a
 * retired variable renders raw (auditRowVm). */
function templateHistoryDialog(
  modal: HTMLElement,
  catalog: CatSection[],
  tpl: TemplateRow,
  notice: (title: string, body: string) => void,
): void {
  void jsonFetch<{ at: string; actor_name: string; action: string;
    changes: { path: string; old: unknown; new: unknown }[] }[]>(
    'GET', `/api/config-templates/${tpl.id}/audit`).then((r) => {
    if (r.status !== 200) return notice('Could not load history', `Refused (${r.status})`);
    const rows = r.data ?? [];
    openDialog(modal, (body, close) => {
      body.appendChild(el('h2', 'hc-dialog-title', `History — ${tpl.name}`));
      body.appendChild(el('p', 'hc-status',
        `${rows.length} recorded change${rows.length === 1 ? '' : 's'}, newest first. Every save, rename, clone and lifecycle action lands here.`));
      const list = el('div', 'hc-ct-review');
      for (const row of rows) {
        const vmRow = auditRowVm(catalog, row);
        const item = el('div', 'hc-ct-revrow');
        const head = el('span', 'hc-ct-revwhere');
        head.textContent = `${vmRow.action} · ${vmRow.actor}`;
        const when = el('time', 'hc-ct-histwhen', new Date(vmRow.when).toLocaleString());
        (when as HTMLTimeElement).dateTime = vmRow.when;
        head.appendChild(when);
        item.appendChild(head);
        for (const line of vmRow.lines) item.appendChild(el('span', 'hc-ct-histline', line));
        list.appendChild(item);
      }
      if (rows.length === 0) list.appendChild(el('p', 'hc-ct-empty', 'No history yet.'));
      body.appendChild(list);
      const actions = el('div', 'hc-dialog-actions');
      // Styles × Themes PR-2: icon-only. Dismissing a read-only viewer is
      // NEUTRAL — hc-btn-primary spent an additive seat on "Close".
      const done = mkIconBtn({ glyph: 'x', label: 'Close', attrs: { 'data-act': 'ok' } });
      done.addEventListener('click', close);
      actions.appendChild(done);
      body.appendChild(actions);
    });
  });
}

export function mountConfigTemplates(root: HTMLElement): () => void {
  const id = location.hash.replace(/^#\/config-templates\/?/, '').split('?')[0] ?? '';
  return id ? mountEditor(root, id) : mountList(root);
}

// ---------------------------------------------------------------------------
// The list

function mountList(root: HTMLElement): () => void {
  const shell = mountShell(root, 'templates');
  // C1: the SAME title class every other Bridge page wears (hc-pagetitle;
  // hc-title had no rule at all — it painted as a raw UA h1). C2/C4: the
  // toolbar moved DOWN out of the pagebar into its own row directly above
  // the grid — search leftmost, roster buttons + the create verb right,
  // the release page's exact placement.
  shell.pagebar.innerHTML = `
    <h1 class="hc-pagetitle">Configuration templates</h1>
    <span class="hc-status" id="hcCtStatus" aria-live="polite"></span>`;
  shell.main.innerHTML = `
    <div class="hc-toolbar" id="hcCtToolbar"></div>
    <div id="hcCtGrid" class="hc-grid"></div>
    <div id="hcCtCardList" class="hc-cardlist"></div>
    <div class="hc-listmore-wrap">${iconBtn({ glyph: 'chevron-down', label: 'Show more', size: 'sm', id: 'hcCtListMore', cls: 'hc-listmore', attrs: { hidden: true } })}</div>`;
  const status = shell.pagebar.querySelector('#hcCtStatus') as HTMLElement;
  const toolbar = shell.main.querySelector('#hcCtToolbar') as HTMLElement;
  const cardListEl = shell.main.querySelector('#hcCtCardList') as HTMLElement;
  const listMore = shell.main.querySelector('#hcCtListMore') as HTMLButtonElement;
  const modal = shell.modal;

  let grid: GridApi<TemplateRow> | null = null;
  let rows: TemplateRow[] = [];
  let catalog: CatSection[] = [];
  let manage = false;
  /** C3 — the releases page's hidden-tail pattern replaces the Show
   * disabled checkbox: rowData is DERIVED from rows × this × the query. */
  let showDisabled = false;
  /** The defs the live grid was built with (home.ts's currentDefs shape). */
  let currentDefs: ColDef<TemplateRow>[] = [];
  let machTeardown: (() => void) | null = null;

  const notice = (title: string, body: string): void => { noticeDialog(modal, { title, body }); };

  // Item E — the release grid's machinery on THIS page's seat: per-column
  // filters, pins, Copy, the column picker, the header context menu, and
  // the row menu, persisted in ui_state grid-preset/'templates' (the
  // EXISTING keyed kind — no server change).
  const mach = adminGridMachinery<TemplateRow>({
    presetKey: 'templates',
    defaultPins: DEFAULT_TEMPLATES_PINS,
    unfiltered: ['actions'],
    columnIds: TEMPLATES_COLUMN_IDS,
    displayValue: (c, r) => templatesDisplayValue(currentDefs, c, r, catalog),
    rows: () => rows,
    rowMenu: (t) => templateRowMenuSpec(t, manage).map((sp): CtxMenuItem => ({
      label: sp.label,
      icon: sp.icon,
      disabled: !sp.enabled,
      ...(sp.tip ? { tip: sp.tip } : {}),
      ...(sp.danger ? { danger: true } : {}),
      run: () => {
        if (sp.id === 'edit') location.hash = `#/config-templates/${t.id}`;
        else if (sp.id === 'clone') cloneDialog(t);
        else if (sp.id === 'history') templateHistoryDialog(modal, catalog, t, notice);
        else if (sp.id === 'set-default') setDefaultDialog(t);
        else if (sp.id === 'disable') disableDialog(t);
        else void doEnable(t);
      },
    })),
  });

  toolbar.innerHTML = `
    <span class="hc-spacer"></span>
    ${renderGridToolbarButtons({
      include: ADMIN_GRID_TOOLBAR,
      tips: { hcRefresh: 'Refresh the template list now' },
    })}
    ${iconBtn({ glyph: 'plus', label: 'New template', intent: 'additive', ctx: 'config-templates-list', size: 'sm', id: 'hcCtNew', cls: 'hc-gtbtn', attrs: { hidden: true } })}`;
  const searchBox = mkSearchBox({
    id: 'hcCtSearch',
    placeholder: 'Search templates',
    label: 'Search templates',
    onInput: () => applyFilter(),
  });
  toolbar.prepend(searchBox.el);
  const search = searchBox.input;
  const newBtn = toolbar.querySelector('#hcCtNew') as HTMLButtonElement;
  newBtn.addEventListener('click', () => createDialog());
  // R32's console grammar: the spin rides the in-flight window; a click
  // while spinning is refused — the spin IS the acknowledgment.
  const refreshBtn = toolbar.querySelector('#hcRefresh') as HTMLButtonElement;
  refreshBtn.addEventListener('click', () => {
    if (refreshBtn.classList.contains('hc-spinning')) return;
    refreshBtn.classList.add('hc-spinning');
    void load().finally(() => refreshBtn.classList.remove('hc-spinning'));
  });
  listMore.addEventListener('click', () => {
    showDisabled = !showDisabled;
    applyFilter();
  });

  function applyFilter(): void {
    const q = search.value.trim().toLowerCase();
    let shown = showDisabled ? rows : rows.filter((t) => !t.disabled);
    if (q) shown = shown.filter((t) => `${t.name} ${t.description}`.toLowerCase().includes(q));
    grid?.setGridOption('rowData', shown);
    // The phone card column (owner ruling 2026-09-06): each template row as a
    // stacked key:value card, tappable to its editor. Built only on a phone;
    // the display vocabulary is the SAME one the grid's filter/copy speak.
    if (phone.isPhone()) {
      renderCardList(cardListEl, gridRowsToCards(shown, {
        columnIds: TEMPLATES_COLUMN_IDS,
        headerName: (c) => currentDefs.find((d) => d.colId === c)?.headerName ?? c,
        displayValue: (c, r) => templatesDisplayValue(currentDefs, c, r, catalog),
        titleCol: 'name',
        skipCols: ['actions'],
        href: (t) => `#/config-templates/${t.id}`,
      }), { empty: q ? 'No templates match your search.' : 'No templates yet.' });
    } else {
      cardListEl.replaceChildren();
    }
    const dis = rows.filter((t) => t.disabled).length;
    status.textContent = `${shown.length} of ${rows.length} template${rows.length === 1 ? '' : 's'}` +
      (dis && !showDisabled ? ` (${dis} disabled hidden)` : '');
    // C3 — the toggle exists exactly when there are hidden rows to show,
    // labelled with the MEASURED size of what it hides (home's paintRows).
    if (dis === 0) {
      listMore.hidden = true;
      showDisabled = false;
      return;
    }
    listMore.hidden = false;
    listMore.textContent = showDisabled ? 'Show less' : `Show more (${dis} disabled)`;
    listMore.setAttribute('aria-expanded', showDisabled ? 'true' : 'false');
  }
  const phone = watchPhone(() => applyFilter());

  function columns(): ColDef<TemplateRow>[] {
    return [
      {
        colId: 'name', headerName: 'Template', flex: 2, minWidth: 240, sort: 'asc',
        // Default first, then name — the template the picker preselects tops the list.
        comparator: (_a, _b, na, nb) => {
          const a = na.data, b = nb.data;
          if (!a || !b) return 0;
          if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
          return a.name.localeCompare(b.name);
        },
        valueGetter: (p) => p.data?.name ?? '',
        cellRenderer: (p: { data?: TemplateRow }) => {
          const wrap = el('span', 'hc-ct-name');
          if (!p.data) return wrap;
          wrap.appendChild(el('span', 'hc-ct-name-text', p.data.name));
          for (const b of templateBadges(p.data)) {
            wrap.appendChild(el('span', `hc-pill hc-ct-badge-${b.toLowerCase()}`, b));
          }
          return wrap;
        },
      },
      {
        colId: 'description', headerName: 'Description', flex: 3, minWidth: 220,
        valueGetter: (p) => p.data?.description ?? '',
      },
      {
        colId: 'overrides', headerName: 'Changed from defaults', flex: 1, minWidth: 150,
        valueGetter: (p) => (p.data ? overrideCount(catalog, p.data.sections) : 0),
        cellRenderer: (p: { data?: TemplateRow }) => {
          const n = p.data ? overrideCount(catalog, p.data.sections) : 0;
          return el('span', n ? 'hc-ct-ovr' : 'hc-ct-ovr hc-ct-ovr-none',
            n ? `${n} setting${n === 1 ? '' : 's'}` : 'pure defaults');
        },
      },
      {
        colId: 'updated', headerName: 'Updated', flex: 1, minWidth: 130,
        comparator: (_a, _b, na, nb) =>
          Date.parse(na.data?.updated_at ?? 0 as never) - Date.parse(nb.data?.updated_at ?? 0 as never),
        valueGetter: (p) => p.data?.updated_at ?? '',
        // C6: the value IS the door to the change log — a chip-that-is-a-
        // button (the Update Available affordance grammar, .hc-chipbtn).
        // The hover says the verb; the absolute instant rides the same tip
        // (the cell must not ALSO carry a native title — double tooltip).
        cellRenderer: (p: { data?: TemplateRow }) => {
          if (!p.data) return el('span');
          const t = p.data;
          const b = el('button', 'hc-chipbtn', relativeTime(t.updated_at ?? null));
          b.type = 'button';
          // DEFERRED to PR-3c, not converted: the cell's VALUE (the relative
          // instant) is this control's whole face, so an icon-only button
          // would delete the column's data. It is the chip primitive's
          // shape — PR-3c owns it.
          b.dataset.btnRole = 'chip';
          b.setAttribute('aria-label', `View change log — updated ${relativeTime(t.updated_at ?? null)}`);
          const abs = absoluteTime(t.updated_at ?? null);
          b.setAttribute('data-tip', `View change log${abs ? `\n${abs}` : ''}`);
          b.addEventListener('click', (e) => {
            e.stopPropagation();
            templateHistoryDialog(modal, catalog, t, notice);
          });
          return b;
        },
      },
      {
        colId: 'actions', headerName: '', flex: 1, minWidth: 170, sortable: false,
        // C5: icon buttons consistent with the users page (both ride the
        // .ibtn primitive since PR-2) — the OLD text is the ONE label the
        // primitive binds to aria-label and data-tip alike, and the
        // inventory still comes from rowActions (one gating, page and menu).
        cellRenderer: (p: { data?: TemplateRow }) => {
          const wrap = el('span', 'hc-actions');
          if (!p.data) return wrap;
          const t = p.data;
          const btn = (label: string, glyph: string, fn: () => void, danger = false): void => {
            // Styles × Themes PR-2: the row verbs ride the primitive now —
            // hc-rowbtn's hand-picked ghost/danger paint is the intent ramp,
            // and hc-rowact carries only the inter-button gap.
            const b = mkIconBtn({
              glyph, label, size: 'sm', cls: 'hc-rowact',
              // Disable is the one danger verb here and its click OPENS
              // disableDialog, which asks with the consequence — arming
              // here too would make disabling a three-click double confirm.
              ...(danger
                ? {
                    intent: 'destructive' as const,
                    noConfirm: 'the click opens the Disable confirmation dialog, which asks with the consequence',
                  }
                : {}),
            });
            b.addEventListener('click', (e) => { e.stopPropagation(); fn(); });
            wrap.appendChild(b);
          };
          for (const a of rowActions(t, manage)) {
            if (a === 'edit') btn(manage ? 'Edit' : 'View', manage ? 'pencil' : 'eye', () => { location.hash = `#/config-templates/${t.id}`; });
            if (a === 'clone') btn('Clone', 'copy', () => cloneDialog(t));
            if (a === 'set-default') btn('Set default', 'star', () => setDefaultDialog(t));
            if (a === 'disable') btn('Disable', 'ban', () => disableDialog(t), true);
            if (a === 'enable') btn('Enable', 'circle-check', () => void doEnable(t));
          }
          return wrap;
        },
      },
    ];
  }

  async function load(): Promise<void> {
    const [cat, list] = await Promise.all([
      jsonFetch<{ sections: CatSection[] }>('GET', '/api/config-catalog'),
      jsonFetch<TemplateRow[]>('GET', '/api/config-templates?includeDisabled=1'),
    ]);
    if (list.status === 401 || list.status === 403) return renderRefusal(root, list.status);
    catalog = cat.data?.sections ?? [];
    rows = list.data ?? [];
    if (!grid) {
      const gridEl = shell.main.querySelector('#hcCtGrid') as HTMLElement;
      currentDefs = columns();
      grid = makeGrid<TemplateRow>(gridEl, {
        // The one decoration pass (item E) — filters + pins over the defs.
        columnDefs: mach.decorate(currentDefs),
        rowData: rows,
        getRowClass: (p) => (p.data?.disabled ? 'hc-row-disabled' : ''),
        onRowDoubleClicked: (p) => { if (p.data) location.hash = `#/config-templates/${p.data.id}`; },
        // Column changes persist into the SAME seat the filters ride.
        onColumnMoved: () => mach.persist(),
        onColumnResized: () => mach.persist(),
        onColumnVisible: () => mach.persist(),
      });
      void mach.wire(grid, {
        gridEl,
        colsBtn: toolbar.querySelector('#hcColsBtn'),
        colsMenu: toolbar.querySelector('#hcColsMenu'),
        resetBtn: toolbar.querySelector('#hcReset'),
      }).then((td) => { machTeardown = td; });
    } else {
      currentDefs = columns();
      grid.setGridOption('columnDefs', mach.decorate(currentDefs));
    }
    applyFilter();
  }

  function createDialog(): void {
    openDialog(modal, (body, close) => {
      body.appendChild(el('h2', 'hc-dialog-title', 'New template'));
      body.appendChild(el('p', 'hc-status', 'Starts at pure catalog defaults — every variable at the value the framework ships.'));
      const name = mkFcText({ label: 'Name', placeholder: 'Name' });
      name.input.setAttribute('data-autofocus', '');
      const desc = mkFcText({ label: 'Description', placeholder: 'Description (optional)' });
      body.appendChild(name.el);
      body.appendChild(desc.el);
      const msg = el('p', 'hc-dialog-msg');
      body.appendChild(msg);
      const actions = el('div', 'hc-dialog-actions');
      const cancel = mkIconBtn({ glyph: 'x', label: 'Cancel' });
      cancel.addEventListener('click', close);
      // THE additive action of THIS dialog. Per-dialog ctx: two stacked
      // dialogs never share a view context, so the one-additive-per-context
      // rule stays true however many the screen grows.
      const save = mkIconBtn({
        glyph: 'plus', label: 'Create', intent: 'additive', ctx: 'new-template-dialog',
      });
      save.addEventListener('click', () => {
        setBtnState(save, 'busy');
        void jsonFetch<TemplateRow & Refusal>('POST', '/api/config-templates',
          { name: name.value, description: desc.value }).then((r) => {
          setBtnState(save, 'enabled');
          if (r.status !== 201) return showDialogMsg(msg, refusalText(r.status, r.data));
          close();
          location.hash = `#/config-templates/${r.data!.id}`;
        });
      });
      actions.appendChild(cancel);
      actions.appendChild(save);
      body.appendChild(actions);
    });
  }

  function cloneDialog(t: TemplateRow): void {
    openDialog(modal, (body, close) => {
      body.appendChild(el('h2', 'hc-dialog-title', `Clone '${t.name}'`));
      body.appendChild(el('p', 'hc-status',
        'Copies every value. The clone starts its own audit trail — history stays with the original.'));
      const name = mkFcText({ label: 'Name', value: `${t.name} (copy)` });
      name.input.setAttribute('data-autofocus', '');
      body.appendChild(name.el);
      const msg = el('p', 'hc-dialog-msg');
      body.appendChild(msg);
      const actions = el('div', 'hc-dialog-actions');
      const cancel = mkIconBtn({ glyph: 'x', label: 'Cancel' });
      cancel.addEventListener('click', close);
      const save = mkIconBtn({
        glyph: 'copy', label: 'Clone', intent: 'additive', ctx: 'clone-template-dialog',
      });
      save.addEventListener('click', () => {
        setBtnState(save, 'busy');
        void jsonFetch<TemplateRow & Refusal>('POST', `/api/config-templates/${t.id}/clone`,
          { name: name.value }).then((r) => {
          setBtnState(save, 'enabled');
          if (r.status !== 201) return showDialogMsg(msg, refusalText(r.status, r.data));
          close();
          location.hash = `#/config-templates/${r.data!.id}`;
        });
      });
      actions.appendChild(cancel);
      actions.appendChild(save);
      body.appendChild(actions);
    });
  }

  function setDefaultDialog(t: TemplateRow): void {
    confirmDialog(modal, {
      title: `Make '${t.name}' the default?`,
      body: 'New releases preselect the default template. The current default is demoted in the same step — there is always exactly one.',
      confirmLabel: 'Set default',
      onConfirm: () => {
        void jsonFetch<Refusal>('POST', `/api/config-templates/${t.id}/set-default`).then((r) => {
          if (r.status !== 200) return notice('Could not set default', refusalText(r.status, r.data));
          return load();
        });
      },
    });
  }

  function disableDialog(t: TemplateRow): void {
    confirmDialog(modal, {
      title: `Disable '${t.name}'?`,
      body: 'It disappears from the release-creation picker. Releases already using it are untouched, and it can be re-enabled here any time.',
      confirmLabel: 'Disable',
      danger: true,
      onConfirm: () => {
        void jsonFetch<Refusal>('POST', `/api/config-templates/${t.id}/disable`).then((r) => {
          if (r.status !== 200) return notice('Could not disable', refusalText(r.status, r.data));
          return load();
        });
      },
    });
  }

  async function doEnable(t: TemplateRow): Promise<void> {
    const r = await jsonFetch<Refusal>('POST', `/api/config-templates/${t.id}/enable`);
    if (r.status !== 200) return notice('Could not enable', refusalText(r.status, r.data));
    return load();
  }

  void shell.whoami.then(({ me }) => {
    manage = canManageTemplates(me);
    if (manage) newBtn.removeAttribute('hidden');
    void load();
  });

  // The machinery's teardown joins the shell's (the column picker's
  // document click dismiss would otherwise accumulate one copy per
  // navigation), and a dialog open across a route change must not leak
  // the engine's document keydown listener (users/audit's exact order).
  return () => {
    phone.teardown();
    machTeardown?.();
    destroyDialog(modal);
    shell.teardown();
  };
}

// ---------------------------------------------------------------------------
// The editor
//
// settings-editor batch (owner-reported): the editor's DOM is built ONCE and
// updated IN PLACE from then on — the previous full innerHTML rebuild on
// every keystroke made every state change a teleport (no reset-button morph,
// no filter exit animation, no sliding nav selector can survive a rebuild).
// The decisions stay in config-templates-vm.ts (resetVm, stepNumber,
// stepperVm, activeSection join the roster); this mount renders them.
//
// The batch, each traced to the owner's words:
//   - the search is mkSearchBox (the ONE search component) and FITS its
//     240px column — the old raw input's 260px min-width slid under the
//     content pane ("search ... clipped by the contents on the righthand
//     side"); the right pane scrolls under the shared element scroll rail
//     ("our custom scroll bar"), left nav untouched;
//   - selects are mkDropdown (R69's custom themed dropdown, portal lane —
//     the cards clip overflow) and numbers are a custom stepper: "custom
//     dropdowns, custom integer up and down controls", keyboard included;
//   - every setting carries the reset button: green disabled check at the
//     default, morphing to an amber reset when the value differs, restoring
//     the default (and the green face) on click. Defaults come from the
//     catalog's v.default — the ONE authority (0 of 156 variables lack one,
//     pinned in the headless suite);
//   - Rename/History are icon buttons wearing their names as tooltips;
//   - scroll-spy: the content pane's scroll drives the nav selection, one
//     absolutely-positioned indicator SLIDING between rows (never a
//     per-row repaint); a nav click scrolls the pane smoothly and slides
//     the selector directly from its previous position;
//   - search prunes rows/cards/nav rows through a two-phase hide (fade,
//     then display:none) and the reverse on return.
// Every motion collapses to instant under prefers-reduced-motion: the CSS
// side rides the registered reduced-motion guard arms; the JS side asks
// prefersReducedMotion() (motion.ts's one shared read) before animating.

function mountEditor(root: HTMLElement, id: string): () => void {
  const shell = mountShell(root, 'templates');
  shell.pagebar.innerHTML = `
    <a class="hc-ct-back" href="#/config-templates">&larr; Templates</a>
    <h1 class="hc-pagetitle" id="hcCtName"></h1>
    <span id="hcCtBadges"></span>
    ${iconBtn({ glyph: 'square-pen', label: 'Rename', size: 'sm', id: 'hcCtRename', attrs: { hidden: true } })}
    ${iconBtn({ glyph: 'history', label: 'History', size: 'sm', id: 'hcCtHistory' })}`;

  const nameEl = shell.pagebar.querySelector('#hcCtName') as HTMLElement;
  const badgesEl = shell.pagebar.querySelector('#hcCtBadges') as HTMLElement;
  const renameBtn = shell.pagebar.querySelector('#hcCtRename') as HTMLButtonElement;
  const modal = shell.modal;

  let catalog: CatSection[] = [];
  let tpl: TemplateRow | null = null;
  let manage = false;
  let editor: ConfigEditorHandle | null = null;

  const notice = (title: string, body: string): void => { noticeDialog(modal, { title, body }); };

  function renderHeader(): void {
    if (!tpl) return;
    nameEl.textContent = tpl.name;
    badgesEl.innerHTML = '';
    for (const b of templateBadges(tpl)) {
      badgesEl.appendChild(el('span', `hc-pill hc-ct-badge-${b.toLowerCase()}`, b));
    }
    if (manage) renameBtn.removeAttribute('hidden');
  }

  function renameDialog(): void {
    if (!tpl) return;
    openDialog(modal, (body, close) => {
      body.appendChild(el('h2', 'hc-dialog-title', 'Rename template'));
      const name = mkFcText({ label: 'Name', value: tpl!.name });
      name.input.setAttribute('data-autofocus', '');
      const desc = mkFcText({ label: 'Description', value: tpl!.description, placeholder: 'Description' });
      body.appendChild(name.el);
      body.appendChild(desc.el);
      const msg = el('p', 'hc-dialog-msg');
      body.appendChild(msg);
      const actions = el('div', 'hc-dialog-actions');
      const cancel = mkIconBtn({ glyph: 'x', label: 'Cancel' });
      cancel.addEventListener('click', close);
      const save = mkIconBtn({
        glyph: 'check', label: 'Save', intent: 'additive', ctx: 'rename-template-dialog',
      });
      save.addEventListener('click', () => {
        setBtnState(save, 'busy');
        void jsonFetch<TemplateRow & Refusal>('PATCH', `/api/config-templates/${tpl!.id}`,
          { name: name.value, description: desc.value }).then((r) => {
          setBtnState(save, 'enabled');
          if (r.status !== 200) return showDialogMsg(msg, refusalText(r.status, r.data));
          tpl = r.data as TemplateRow;
          close();
          renderHeader();
        });
      });
      actions.appendChild(cancel);
      actions.appendChild(save);
      body.appendChild(actions);
    });
  }

  // The audit-trail viewer: ONE dialog (templateHistoryDialog above) —
  // the list's Updated cell and row menu open the same implementation
  // (item C6), so the two screens cannot drift.
  (shell.pagebar.querySelector('#hcCtHistory') as HTMLButtonElement)
    .addEventListener('click', () => {
      if (tpl) templateHistoryDialog(modal, catalog, tpl, notice);
    });
  renameBtn.addEventListener('click', () => renameDialog());

  void (async () => {
    const [{ me }, cat, t] = await Promise.all([
      shell.whoami,
      jsonFetch<{ sections: CatSection[] }>('GET', '/api/config-catalog'),
      jsonFetch<TemplateRow & Refusal>('GET', `/api/config-templates/${id}`),
    ]);
    manage = canManageTemplates(me);
    if (t.status !== 200) {
      if (t.status === 401 || t.status === 403) return renderRefusal(root, t.status);
      shell.main.innerHTML = '';
      shell.main.appendChild(el('p', 'cfg-empty', refusalText(t.status, t.data)));
      return;
    }
    catalog = cat.data?.sections ?? [];
    tpl = t.data as TemplateRow;
    renderHeader();
    // THE editor (Helm settings re-implementation, owner ruling 2026-09-08):
    // the shared component in pi-stack — the Helm's settings tab mounts the
    // very same module. This screen owns only its data plane: the catalog
    // and the template row in, PATCH /api/config-templates/<id> out.
    editor = mountConfigEditor({
      header: shell.pagebar,
      body: shell.main,
      modal,
      vocab: HC_VOCAB,
      catalog,
      saved: tpl.sections,
      canManage: manage,
      searchPlaceholder: 'Search 150+ settings',
      discardConfirm: 'Discard every unsaved edit in this template?',
      onSave: async (sparse) => {
        const r = await jsonFetch<TemplateRow & Refusal>('PATCH', `/api/config-templates/${tpl!.id}`, { sections: sparse });
        if (r.status !== 200) return { ok: false, error: refusalText(r.status, r.data) };
        tpl = r.data as TemplateRow;
        return { ok: true, saved: tpl.sections };
      },
    });
  })();

  return () => {
    editor?.destroy();
    destroyDialog(modal);
    shell.teardown();
  };
}
